import {
  type ParsedSetLevelPlaylist,
  parseSolinkPlaylist,
  type VideoSegment,
  type VideoUrlProps,
} from '@solinkcorp/playlist-parser';
import { type ScaleLinear } from 'd3-scale';
import { LRUCache } from 'lru-cache';
import * as promiseTools from 'promise-tools';
import {
  CameraNotAccessibleError,
  isAuthFailureStatus,
} from 'src/core/video/cameraNotAccessibleError';
import { getVideoTranslations } from 'src/core/video/videoTranslations';
import I18nError from 'utils/I18nError';

export const MAX_PLAYLISTS_TO_CACHE = 1000;

/**
 * If the `end` of the requested playlist is after `now - SHORT_CACHE_TIME_CUTOFF_IN_MS`,
 * we will only cache the playlist for the SHORT_CACHE_DURATION_IN_MS.
 */
export const SHORT_CACHE_TIME_CUTOFF_IN_MS = 1000 * 60 * 30;
export const SHORT_CACHE_DURATION_IN_MS = 10 * 1000;
export const LEVEL_CACHE_DURATION_IN_MS = 1000 * 60 * 5;

export interface CachedPlaylist {
  /** The m3u8 for this playlist. */
  readonly m3u8: string;
  /**
   * Segments for the playlist.  Note that if the playlist is a set level playlist,
   * this will contain segments from the first child playlist.
   */
  readonly segments: readonly VideoSegment[];
  /**
   * Time-translation function associated with the segments.
   */
  readonly ttf: ScaleLinear<number, number>;
}

export interface CachedSetLevelPlaylist extends ParsedSetLevelPlaylist {
  readonly segments: readonly VideoSegment[];
  readonly ttf: ScaleLinear<number, number>;
}

export interface PlaylistResult {
  /** The parsed playlist. */
  playlist: CachedPlaylist;
  /** Headers returned with the playlist. */
  headers: Headers;
}

interface CacheEntry {
  /**
   * Promise which resolves to the loaded playlist.  We cache a Promise instead
   * of the actual loaded playlist, because this way if two different callers
   * ask for the same URL at the same time, we'll only fetch and parse it once.
   */
  result: Promise<PlaylistResult>;
  loaded: boolean;
  expires: number;
}

export interface PlaylistCache {
  loadPlaylist(
    urlProps: VideoUrlProps,
    options?: { timeout?: number; signal?: AbortSignal },
  ): Promise<{
    playlist: CachedPlaylist;
    headers: Headers;
  }>;

  hasLoadedPlaylist(url: string): boolean;
}

export class PlaylistCacheImpl implements PlaylistCache {
  private _cacheByUrl = new LRUCache<string, CacheEntry>({
    max: MAX_PLAYLISTS_TO_CACHE,
  });

  public cacheHits: number = 0;
  public cacheMisses: number = 0;

  private _getByUrl(url: string) {
    const result = this._cacheByUrl.get(url);
    if (result && Date.now() >= result.expires) {
      return undefined;
    }
    return result;
  }

  /**
   * Load a playlist, fetch it from the cache if possible.
   *
   * If the playlist URL is for a set-level playlist (e.g. a playlist with
   * VTT subtitles), this will return a cache entry with the content of the
   * set-level playlist, but will also load segments and the TTF function
   * from the child playlist.
   */
  loadPlaylist(
    urlProps: VideoUrlProps,
    options: { timeout?: number; signal?: AbortSignal } = {},
  ): Promise<{
    playlist: CachedPlaylist;
    headers: Headers;
  }> {
    const { url, authHeaders } = urlProps;

    const parsed = new URL(url);
    const rawEnd = parsed.searchParams.get('end');
    const end = rawEnd ? parseInt(rawEnd, 10) : undefined;
    const useShortCache = !end || end > Date.now() - SHORT_CACHE_TIME_CUTOFF_IN_MS;
    let expires = useShortCache ? Date.now() + SHORT_CACHE_DURATION_IN_MS : Infinity;

    if (parsed.searchParams.get('level')) {
      expires = Date.now() + LEVEL_CACHE_DURATION_IN_MS;
    }

    const cachable = url.includes('.m3u8') && !url.includes('/live.m3u8');
    const cached = cachable ? this._getByUrl(url) : undefined;
    if (cached) {
      this.cacheHits++;
      return cached.result;
    }
    if (cachable) {
      this.cacheMisses++;
    }

    const fetchPlaylist = async (url: string) => {
      // prevent device from setting cookie
      let result = fetch(url, {
        headers: authHeaders,
        credentials: 'include',
        signal: options.signal,
      });

      if (options.timeout) {
        result = promiseTools.timeout(result, options.timeout);
      }

      const response = await checkResponse(result);
      const m3u8 = await response.text();
      const playlist = parseSolinkPlaylist(m3u8);

      return { headers: response.headers, playlist };
    };

    const cacheEntry = {
      expires,
      loaded: false,
    } as CacheEntry;

    cacheEntry.result = fetchPlaylist(url).then(async ({ headers, playlist }) => {
      if (playlist.type === 'playlist') {
        cacheEntry.loaded = true;
        return { playlist, headers };
      } else if (playlist.type === 'set-level') {
        // This is a set-level playlist, with other playlists nested inside.
        // Grab segments from the first child playlist, and use these for
        // segments and the TTF function.
        if (playlist.childM3u8.length === 0) {
          throw new Error('set-level playlist has no m3u8 files!');
        }
        const childPlaylistUrl = new URL(playlist.childM3u8[0], url).toString();
        const { playlist: segmentPlaylist } = await this.loadPlaylist({
          ...urlProps,
          url: childPlaylistUrl,
        });

        cacheEntry.loaded = true;
        return {
          playlist: {
            m3u8: playlist.m3u8,
            segments: segmentPlaylist.segments,
            ttf: segmentPlaylist.ttf,
          },
          headers,
        };
      }
      throw new Error(`Unknown playlist type ${(playlist as any).type}`);
    });

    if (cachable) {
      this._cacheByUrl.set(url, cacheEntry);

      // If loading fails, remove this entry from the cache so we'll try again
      // if someone tries to load this URL again.
      cacheEntry.result.catch(() => {
        this._cacheByUrl.delete(url);
      });
    }

    return cacheEntry.result;
  }

  /**
   * Returns true if a playlist is cached and loaded.
   */
  hasLoadedPlaylist(url: string): boolean {
    return this._getByUrl(url)?.loaded || false;
  }
}

async function checkResponse(responsePromise: Promise<Response>) {
  try {
    const response = await responsePromise;

    if (!response.ok) {
      // A 401/403 is an access failure scoped to this camera stream (commonly a
      // relay stream-token rejection from client/relay clock drift), not the app
      // session. Surface it as a distinct per-tile "not accessible" error and
      // never log the whole client out. See SL-15542.
      if (isAuthFailureStatus(response.status)) {
        console.error(`list video error - camera not accessible [${response.status}]`);
        throw new CameraNotAccessibleError(response.status);
      }

      let errorMessage = '';
      try {
        const data = await response.text();
        try {
          const jsonData = JSON.parse(data);
          errorMessage = jsonData.error;
        } catch {
          // Ignore
        }
        if (!errorMessage && data) {
          errorMessage = data;
        }
      } catch {
        // Ignore
      }

      const message = `[${response.status} ${response.statusText}] - ${errorMessage}`;
      console.error(`list video error - ${message}`);

      throw new I18nError(
        message,
        (localeCode) => getVideoTranslations(localeCode).problemLoadingResource,
      );
    }

    return response;
  } catch (err: any) {
    // Preserve already-typed video errors (e.g. CameraNotAccessibleError) so the
    // tile can render their specific message; only wrap raw/unknown errors.
    if (err instanceof I18nError) {
      throw err;
    }
    throw new I18nError(
      err.message,
      (localeCode) => getVideoTranslations(localeCode).problemLoadingResource,
    );
  }
}
